const steps = [
  {
    id: "01",
    label: "Ingest",
    title: "Multi-Source Data Ingestion",
    description:
      "Polls SEC EDGAR filings, company IR press releases, and retail news APIs. All raw payloads are stored verbatim for full audit traceability.",
    tags: ["SEC EDGAR", "IR Feeds", "News APIs"],
    color: "text-cyan-400",
    border: "border-cyan-900/50",
    bg: "bg-cyan-950/20",
  },
  {
    id: "02",
    label: "Normalize",
    title: "Normalization & Ticker Mapping",
    description:
      "Extracts tickers via NER and symbol lookup. Attaches source credibility tiers and event-type hints (earnings, M&A, regulatory). Produces structured NormalizedItems.",
    tags: ["NER", "Ticker Mapping", "Source Tiers"],
    color: "text-blue-400",
    border: "border-blue-900/50",
    bg: "bg-blue-950/20",
  },
  {
    id: "03",
    label: "Cluster",
    title: "De-duplication & Event Threading",
    description:
      "Hash-first de-duplication prevents double-triggering. Related headlines are grouped into persistent EventThreads that track first-seen, last-updated, and rolling confidence.",
    tags: ["Hash De-dup", "EventThreads", "Confidence Scoring"],
    color: "text-violet-400",
    border: "border-violet-900/50",
    bg: "bg-violet-950/20",
  },
  {
    id: "04",
    label: "Score",
    title: "Deterministic Scoring Engine",
    description:
      "Computes three independent scores — credibility, novelty, and options impact — and combines them into a priority rating: IGNORE, MONITOR, or ANALYZE. No LLM involved at this stage.",
    tags: ["Credibility", "Novelty", "Impact", "No LLM"],
    color: "text-amber-400",
    border: "border-amber-900/50",
    bg: "bg-amber-950/20",
  },
  {
    id: "05",
    label: "Classify",
    title: "LLM Event Classification",
    description:
      "Invoked only for ANALYZE-priority events. The LLM receives headline, source context, event thread history, and a live options snapshot. Outputs strict JSON: directionality, volatility risk, and recommended playbook.",
    tags: ["LLM", "Schema-Validated", "Options Snapshot"],
    color: "text-rose-400",
    border: "border-rose-900/50",
    bg: "bg-rose-950/20",
  },
  {
    id: "06",
    label: "Route & Act",
    title: "Playbook Routing & Trade Intent",
    description:
      "A deterministic router combines scores, LLM classification, and market features to select: LONG_GAMMA, DIRECTIONAL_BREAKOUT, MONITOR_WAIT, or STAND_DOWN. Approved events produce broker-agnostic TradeIntents.",
    tags: ["Long Gamma", "Directional", "Stand Down", "Risk Guardrails"],
    color: "text-green-400",
    border: "border-green-900/50",
    bg: "bg-green-950/20",
  },
];

export default function Pipeline() {
  return (
    <section id="pipeline" className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center mb-16">
          <p className="text-sm font-semibold uppercase tracking-widest text-huskly-teal mb-4">
            How It Works
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            End-to-End Intelligence Pipeline
          </h2>
          <p className="mt-4 text-slate-400 leading-relaxed">
            Every event travels through a deterministic, auditable pipeline from
            raw news to actionable trade intent. Nothing is left to chance.
          </p>
        </div>

        {/* Pipeline flow label */}
        <div className="hidden lg:flex items-center justify-center mb-8 gap-2 text-xs text-slate-600 font-mono">
          <span>RawItem</span>
          <span>→</span>
          <span>NormalizedItem</span>
          <span>→</span>
          <span>EventThread</span>
          <span>→</span>
          <span>ScoredEvent</span>
          <span>→</span>
          <span>Decision</span>
          <span>→</span>
          <span className="text-huskly-green">TradeIntent</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {steps.map((step) => (
            <div
              key={step.id}
              className={`relative rounded-xl border ${step.border} ${step.bg} p-6 flex flex-col gap-4`}
            >
              <div className="flex items-start justify-between">
                <span
                  className={`text-xs font-mono font-bold ${step.color} tracking-widest`}
                >
                  {step.id} / {step.label.toUpperCase()}
                </span>
              </div>
              <div>
                <h3 className="text-base font-semibold text-white mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {step.description}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-auto">
                {step.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2 py-0.5 rounded-full bg-navy-800 text-slate-400 border border-navy-600"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
